import React, {useMemo, useState} from 'react';
import {DirectionalHint, IconButton, Spinner, SpinnerSize, Text, TooltipHost, useTheme} from '@fluentui/react';
import {IWorkspaceItem} from '../../models/Workspace';
import {useGetProductBundleItemsQuery} from '../../store/endpoints/ProductBundleEndpoints';
import {calculateBundleItemFigure} from '../../models/ProductBundles';

interface IBundlePeekProps {
    /** The live bundle to list when there is no snapshot (or no workspace line at all). */
    bundleId?: string;
    /** Heading for the flyout; defaults to the workspace line's bundle name. */
    name?: string;
    /** A workspace bundle line: its captured snapshot (if any) wins over the live bundle. */
    item?: IWorkspaceItem;
    /** Opens the full bundle view (the same action as clicking the bundle's name). */
    onOpen?: () => void;
    /** Render the flyout around your own trigger instead of the default eye button. */
    children?: React.ReactElement;
    /** Where the flyout opens relative to the trigger (default: to the right). */
    directionalHint?: DirectionalHint;
    /** 'noCost' lists only the lines nothing puts a number on (the card's "n items, cost unknown" note). */
    filter?: 'noCost';
}

const COST = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 2});

interface IPeekLine {
    key: string;
    name: string;
    quantity?: number;
    /** Optional lines the customer did not pick render muted. */
    included: boolean;
    optional: boolean;
    group?: string;
    /** Unit figure of the live line; undefined when nothing puts a number on it (or it is a snapshot). */
    cost?: number;
    /** The figure is the line's hand-entered Override Price, not a vendor cost. */
    overridePrice?: boolean;
    /** Live line nothing puts a number on: no vendor cost and no override of either kind. */
    noCost?: boolean;
}

/**
 * A hover flyout listing what a bundle holds. On the workspace grid it wraps a small eye beside the
 * bundle's name and prefers the snapshot captured with the line (so it matches what was ordered or
 * prepared); elsewhere (the bundle picker) it wraps whatever trigger it is given and lists the live
 * bundle's items, fetched on first hover.
 */
const BundlePeek: React.FC<IBundlePeekProps> = ({bundleId, name, item, onOpen, children, directionalHint, filter}) => {
    const theme = useTheme();
    const [peeked, setPeeked] = useState(false);
    const snapshot = item?.bundleDefinition?.products;
    const liveId = bundleId ?? item?.sourceEntityId;
    const needsLive = !snapshot?.length && !!liveId;
    const live = useGetProductBundleItemsQuery(liveId ?? '', {skip: !peeked || !needsLive});

    const lines = useMemo<IPeekLine[]>(() => {
        if (item && snapshot?.length) {
            const chosen = item.preparedConfiguration?.length ? item.preparedConfiguration : item.bundleSelections;
            const chosenById = new Map((chosen ?? []).map((s) => [s.productId ?? '', s.quantity]));
            const groupOf = new Map<string, string>();
            (item.bundleDefinition?.groups ?? []).forEach((g) => (g.products ?? []).forEach((p) => groupOf.set(p, g.name ?? '')));
            return snapshot.map((p, i) => {
                const optional = !!p.optionality && p.optionality.toLowerCase() !== 'required';
                const picked = chosenById.has(p.productId ?? '');
                return {
                    key: p.productId ?? `${i}`,
                    name: p.name ?? p.productId ?? 'Item',
                    quantity: picked ? (chosenById.get(p.productId ?? '') ?? p.quantity) : p.quantity,
                    included: !optional || picked || (chosen?.length ? false : p.isDefault !== false),
                    optional,
                    group: groupOf.get(p.productId ?? '') || undefined,
                };
            });
        }
        const all = (live.data?.items ?? []).map((bi, i) => {
            const figure = calculateBundleItemFigure(bi);
            return {
                key: bi.id ?? bi.productId ?? `${i}`,
                name: bi.sourceItem?.name ?? bi.productId,
                quantity: bi.details?.quantity,
                included: true,
                optional: false,
                cost: figure?.low,
                overridePrice: figure?.isOverridePrice,
                noCost: figure === undefined,
            };
        });
        return filter === 'noCost' ? all.filter((l) => l.noCost) : all;
    }, [item, snapshot, live.data, filter]);
    const showCosts = !snapshot?.length && lines.some((l) => l.cost !== undefined || l.noCost);

    const content = (
        <div style={{maxWidth: 360, minWidth: 220}}>
            <Text variant='mediumPlus' block styles={{root: {fontWeight: 600, marginBottom: 4}}}>
                {filter === 'noCost' ? 'Cost unknown' : (name ?? item?.adhoc?.name ?? 'Bundle')}
            </Text>
            {filter === 'noCost' && (
                <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginBottom: 4}}}>No vendor has a cost for these and no Override Price is set, so they are left out of the range.</Text>
            )}
            {needsLive && live.isFetching && <Spinner size={SpinnerSize.xSmall} label='Loading contents…' labelPosition='right' />}
            {!live.isFetching && lines.length === 0 && (
                <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary}}}>No items in this bundle.</Text>
            )}
            {lines.map((line) => (
                <div key={line.key} style={{display: 'flex', gap: 8, alignItems: 'baseline', opacity: line.included ? 1 : 0.55, lineHeight: '18px'}}>
                    <Text variant='small' styles={{root: {minWidth: 28, textAlign: 'right', color: theme.palette.neutralSecondary}}}>
                        {line.quantity !== undefined ? `${line.quantity} ×` : ''}
                    </Text>
                    <Text variant='small' styles={{root: {textDecoration: line.included ? undefined : 'line-through', flexGrow: 1, minWidth: 0}}}>
                        {line.name}
                        {line.group && <span style={{color: theme.palette.neutralSecondary}}> · {line.group}</span>}
                        {line.optional && line.included && <span style={{color: theme.palette.neutralSecondary}}> · optional</span>}
                    </Text>
                    {showCosts && filter !== 'noCost' && (
                        <Text variant='small'
                            styles={{root: {whiteSpace: 'nowrap', color: line.noCost ? theme.palette.neutralTertiary : theme.palette.neutralPrimary, fontStyle: line.noCost ? 'italic' : undefined}}}
                            title={line.overridePrice ? 'The Override Price set on the bundle, which stands in for any vendor cost'
                                : line.cost !== undefined ? 'Cheapest vendor unit cost, in-stock vendors preferred'
                                : 'No vendor carries a cost for this line'}>
                            {line.cost !== undefined ? COST.format(line.cost) : 'cost unknown'}
                            {line.overridePrice && <span style={{color: theme.palette.neutralSecondary}}> override</span>}
                        </Text>
                    )}
                </div>
            ))}
            {onOpen && filter !== 'noCost' && (
                <Text variant='small' block styles={{root: {marginTop: 6, color: theme.palette.neutralSecondary}}}>Click to open the bundle</Text>
            )}
        </div>
    );

    return (
        <TooltipHost
            content={content}
            directionalHint={directionalHint ?? DirectionalHint.rightCenter}
            calloutProps={{gapSpace: 4}}
            onTooltipToggle={(visible) => { if (visible) setPeeked(true); }}
        >
            {children ?? (
                <IconButton
                    iconProps={{iconName: 'View'}}
                    ariaLabel='Peek at bundle contents'
                    onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
                    styles={{root: {height: 22, width: 22}, icon: {fontSize: 12, color: theme.palette.themePrimary}}}
                />
            )}
        </TooltipHost>
    );
};

export default BundlePeek;
